import _ from "lodash";
import axios, { AxiosRequestConfig, Method } from "axios";
import qs from "qs";
import { SERVER_URL } from "./config"

type StrapiListResponse<T> = {
  data: T[];
  meta: { pagination: { page: number; pageCount: number } };
};

class Server {
  token?: string;

  setToken(token?: string) {
    this.token = token;
  }

  private async _request<T = any>(
    method: Method | string,
    path: string,
    options: AxiosRequestConfig = {}
  ): Promise<T> {
    const { data } = await axios.request<T>({
      method,
      baseURL: SERVER_URL,
      url: path,
      ...options,
      headers: {
        ...(this.token ? { Authorization: `Bearer ${this.token}` } : {}),
        ...(options.headers ?? {}),
      },
    });
    return data;
  }

  private async request<T = any>(
    method: Method | string,
    path: string,
    data?: any
  ): Promise<T> {
    return this._request<T>(
      method,
      path,
      _.toLower(String(method)) === "get"
        ? {
            params: data,
            paramsSerializer: {
              serialize: (params) =>
                qs.stringify(params, { encodeValuesOnly: true }),
            },
          }
        : { data }
    );
  }

  // backward-compatible typo aliases
  async _resuest<T = any>(
    method: Method | string,
    path: string,
    options: AxiosRequestConfig = {}
  ) {
    return this._request<T>(method, path, options);
  }

  async resuest<T = any>(method: Method | string, path: string, data?: any) {
    return this.request<T>(method, path, data);
  }

  get<T = any>(path: string, data?: any) {
    return this.request<T>("GET", path, data);
  }

  post<T = any>(path: string, data?: any) {
    return this.request<T>("POST", path, data);
  }

  put<T = any>(path: string, data?: any) {
    return this.request<T>("PUT", path, data);
  }

  delete<T = any>(path: string, data?: any) {
    return this.request<T>("DELETE", path, data);
  }

  postForm<T = any>(
    path: string,
    data?: FormData,
    config: AxiosRequestConfig = {}
  ) {
    return this._request<T>("POST", path, { data, ...config });
  }

  async findAll<T = any>(path: string, params?: any) {
    const result: T[] = [];
    let page = 1;
    let total = 1;

    do {
      const { data, meta } = await this.get<StrapiListResponse<T>>(path, {
        pagination: { page, pageSize: 25 },
        ...(params ?? {}),
      });
      result.push(...data);
      page = meta.pagination.page + 1;
      total = meta.pagination.pageCount;
    } while (page <= total);

    return result;
  }

  fetchUser<T = any>(token: string) {
    return this._request<T>("GET", "/users/me", {
      headers: { Authorization: `Bearer ${token}` },
      params: { populate: "*" },
      paramsSerializer: {
        serialize: (params) =>
          qs.stringify(params, { encodeValuesOnly: true }),
      },
    });
  }
}

export const server = new Server();
export default server;